import { useState, useEffect, useMemo } from 'react';
import { getSupabase } from '../../../components/supabaseClient';
import { bucketName_files } from '../../../components/env';
import { isProcessed } from './processedLedger';
import { buildReportsFolder } from '../../../utils/reportsPath';
import { groupReportsByClassification } from './reportCategories';
import { recordingLabel } from '../../../utils/recordingLabel';

function audioItems(patient, ledger) {
  const audios = Array.isArray(patient?.more_audios) ? patient.more_audios : [];
  return audios.map((a, index) => {
    const id = String(a.session_id ?? a.audio_index ?? a.url);
    const duration = a.duration ?? a.audio_duration ?? a.audioDuration
      ?? a.duration_seconds ?? a.durationSeconds ?? a.metadata?.duration ?? null;
    return {
      type: 'audio', id,
      label: recordingLabel(a, index + 1),
      date: a.created_at || a.recorded_at || a.uploaded_at || a.date || null,
      duration,
      processed: isProcessed(ledger, 'audio', id),
    };
  });
}

function sourceId(value) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'object') {
    return sourceId(value.submission_id ?? value.id ?? value.form_id ?? value.url);
  }
  const text = String(value);
  const submissionMatch = text.match(/\/([^/]+)\/submission\/([^/?#]+)/);
  if (submissionMatch) return submissionMatch[2];
  return text;
}

function formItems(patient, ledger) {
  const subs = Array.isArray(patient?.submissions_array) ? patient.submissions_array : [];
  return subs.map((s, i) => {
    const id = sourceId((s && (s.submission_id || s.form_id || s.id || s.url)) || s || `form_${i}`);
    return {
      type: 'form', id,
      label: (s && (s.form_name || s.PatientName)) || `Form ${i + 1}`,
      date: (s && s.created_at) || null,
      processed: isProcessed(ledger, 'form', id),
    };
  });
}

function group(type, label, items) {
  return { key: type, type, label, items, newCount: items.filter((i) => !i.processed).length };
}

function reportGroups(reports) {
  // Split the single Reports list into sub-groups keyed by classification.
  // Each sub-group keeps type='report' so the existing selection / apply
  // plumbing keeps working — only the visual grouping changes. Shared
  // helper so the Right-panel Reports tab uses identical labels + order.
  return groupReportsByClassification(reports).map((g) => ({
    key: `report:${g.classification}`,
    type: 'report',
    subtype: g.classification,
    label: g.label,
    items: g.items,
    newCount: g.items.filter((i) => !i.processed).length,
  }));
}

export function useVisitSources(patient, ledger) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const folder = patient?.reports || buildReportsFolder(patient);
        if (!folder) { if (!cancelled) setReports([]); return; }
        const sb = await getSupabase();
        const { data } = await sb.storage.from(bucketName_files || 'files').list(folder);
        const files = (data || []).filter((d) => d.name && d.name.includes('.'));
        const paths = files.map((f) => `${folder}/${f.name}`);
        let meta = {};
        if (paths.length) {
          const { data: rows } = await sb
            .from('file_classification_cache')
            .select('file_path, classification, created_at')
            .in('file_path', paths);
          for (const r of rows || []) meta[r.file_path] = r;
        }
        if (cancelled) return;
        setReports(files.map((f) => {
          const path = `${folder}/${f.name}`;
          return {
            type: 'report', id: path, label: f.name,
            date: meta[path]?.created_at || null,
            classification: meta[path]?.classification || 'Other',
            processed: isProcessed(ledger, 'report', path),
          };
        }));
      } catch (_e) {
        if (!cancelled) setReports([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [patient, ledger]);

  // Memoize so consumers get a stable reference across the clinic page's frequent
  // re-renders (autosave, collab presence, comment bubbles). Without this, the
  // Process modal received a new `groups` array every render and re-seeded itself,
  // wiping the user's expand/checkbox state.
  // Reports are sub-bucketed by classification (Labs / Imaging / Medical
  // Records / Other Documents) so the clinician sees what they're about to
  // process — matches Leejoe's 9-sample fixture taxonomy.
  const groups = useMemo(() => [
    group('audio', 'Audio', audioItems(patient, ledger)),
    group('form', 'Forms', formItems(patient, ledger)),
    ...reportGroups(reports),
  ], [patient, ledger, reports]);
  const unprocessedCount = useMemo(
    () => groups.reduce((n, g) => n + g.newCount, 0),
    [groups],
  );
  return { groups, unprocessedCount, loading };
}
